// Géométrie de la courbe de capital (sparkline de la page détail d'un compte).
//
// Extrait du composant pour être TESTABLE : les deux défauts corrigés ici ne
// se voyaient qu'à l'œil, sur un compte ayant la bonne forme de données.
//
// DÉFAUT 1 — un seul point ne dessine rien.
// La série est agrégée PAR JOUR. Un compte dont tous les trades tombent le même
// jour produit un unique point, et `<polyline>` sans second sommet n'a aucun
// segment à tracer : la carte s'affichait vide alors que la donnée était là.
// Le cas n'a rien d'exotique — c'est tout premier jour de trading.
//
// DÉFAUT 2 — le libellé du plancher, collé au bord.
// Quand le plancher de drawdown est le minimum de l'échelle (le cas courant :
// il est sous la courbe), son ordonnée tombe sur le bord bas du graphe et le
// libellé se retrouvait écrasé contre la ligne. On expose donc de quel côté le
// poser.

export const SPARKLINE_WIDTH = 600;
export const SPARKLINE_HEIGHT = 120;
export const SPARKLINE_PAD = 8;

/**
 * Sous cette fraction de hauteur, le plancher est trop près du bord HAUT pour
 * qu'un libellé tienne au-dessus : on le bascule en dessous.
 */
const LABEL_FLIP_THRESHOLD = 0.15;

export interface SparklineGeometryInput {
  /** Soldes successifs, un par jour de trading. */
  balances: number[];
  initialBalance: number;
  /** Plancher de drawdown, quand le palier en définit un. */
  drawdownFloor: number | null;
}

export interface SparklineGeometry {
  /** Sommets `"x,y"` prêts pour `<polyline points>`. Toujours >= 2 entrées. */
  points: string[];
  /** Ordonnée du plancher dans le repère du SVG. Null s'il n'y en a pas. */
  floorY: number | null;
  /** Le libellé du plancher doit-il être posé SOUS la ligne ? */
  labelBelow: boolean;
  /** Hauteur du repère, pour convertir `floorY` en pourcentage côté CSS. */
  height: number;
}

export function buildSparklineGeometry(
  input: SparklineGeometryInput,
): SparklineGeometry {
  const { balances, initialBalance, drawdownFloor } = input;

  const allValues = [initialBalance, ...balances];
  if (drawdownFloor !== null) allValues.push(drawdownFloor);

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  // `|| 1` : une série parfaitement plate donnerait un range nul, donc des
  // ordonnées NaN et une courbe invisible.
  const range = max - min || 1;

  const w = SPARKLINE_WIDTH;
  const h = SPARKLINE_HEIGHT;
  const pad = SPARKLINE_PAD;

  const toY = (val: number) => h - pad - ((val - min) / range) * (h - pad * 2);

  // Un point unique devient un SEGMENT horizontal pleine largeur : la lecture
  // honnête d'une série d'un jour — le capital vaut cela, aucune tendance
  // n'est encore observable.
  const points =
    balances.length === 1
      ? [`${pad},${toY(balances[0])}`, `${w - pad},${toY(balances[0])}`]
      : balances.map((b, i) => {
          const x = pad + (i / (balances.length - 1)) * (w - pad * 2);
          return `${x},${toY(b)}`;
        });

  const floorY = drawdownFloor !== null ? toY(drawdownFloor) : null;

  return {
    points,
    floorY,
    labelBelow: floorY !== null && floorY / h < LABEL_FLIP_THRESHOLD,
    height: h,
  };
}
